import React, { useEffect, useCallback, useState } from "react"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import moment from "moment"
import { Divider, Flex, Button, Spin, Form } from "antd"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import styles from "./ExperienceBooking.module.css"
import { routes } from "router"
import { BOOKING_MODES } from "pagesV2/pro/Bookings/constants"
import GuestsSelector from "./GuestSelector"
import DateSelector from "./DateSelector"
import { fetchExperienceBookingDetails, updateBookingSelections, resetBookingSelections } from "store/experiences"
import { oldRoutes } from "router/old-routes"
import { numberToCurrency } from "helpers/string"
import FlatRatePriceTooltip from "ui/FlatRatePriceTooltip"
import { applyPlatformFeeSetting } from "pagesV2/pro/Bookings/helpers/calculateBookingTotal"
import PricingDetailsTooltip from "ui/PricingDetailsTooltip"
import { Text } from "ui/Typography"
import PromoCodeInput from "ui/PromoCodeInput"

const INFO_DATA = [
  { label: "Date", value: "Add Date" },
  { label: "Guests", value: "Add Guests" },
  { label: "Trip Length", value: "Add Trip Length" },
  { label: "Time", value: "Add Time" }
]

const EMPTY_COUNTS = {
  main: { adults: 0, children: 0, infants: 0, passengers: 0 },
  extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const PillSelector = ({ label, options, selectedValue, onSelect, error, errorMessage, activeTab, embedded }) => {
  const isDisabled = activeTab === 2 

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>{label}</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedValue || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>{label}</span>
        <div className={styles.pillContainer + " " + (embedded ? styles.embeddedPillContainer : "")}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`${styles.pill} ${selectedValue === option.value ? styles.selected : ""}`}
              onClick={() => onSelect(option)}
            >
              {option.value}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          {errorMessage}
        </Text>
      )}
    </div>
  )
}

PillSelector.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string })).isRequired,
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const TimeSlotSelector = ({ slots, selectedSlot, onSelect, error, activeTab, embedded }) => {
  const isDisabled = activeTab === 2

  if (isDisabled) {
    return (
      <Flex className={styles.inputContainer}>
        <span className={styles.inputLabel}>Time</span>
        <span className={styles.selectedInputLink} style={{ color: "var(--color-text-secondary-dark)" }}>
          {selectedSlot || "—"}
        </span>
      </Flex>
    )
  }

  return (
    <div className={styles.stepFadeIn}>
      <div className={styles.stepSection}>
        <span className={styles.stepSectionLabel}>Time</span>
        {slots.length ? (
          <div className={styles.pillContainer}>
            {slots.map((slot) => (
              <button
                key={slot}
                type="button"
                className={`${styles.pill} ${selectedSlot === slot ? styles.selected : ""} ${embedded ? styles.embeddedTimePillContainer : ""}`}
                onClick={() => onSelect(slot)}
              >
                {slot}
              </button>
            ))}
          </div>
        ) : (
          <Text style={{ color: "var(--color-text-secondary-dark)" }}>No Time Slots</Text>
        )}
      </div>
      {error && (
        <Text color="var(--color-error)" style={{ fontSize: "var(--font-size-s)" }}>
          Time is required.
        </Text>
      )}
    </div>
  )
}

TimeSlotSelector.propTypes = {
  slots: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSlot: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  error: PropTypes.bool,
  activeTab: PropTypes.number,
  embedded: PropTypes.bool
}

const PriceBreakdown = ({ breakdown, amount, depositAmount, balanceAmount, selectedPayment, bookingDate, appliedPromos = [] }) => {
  const discountTotal = appliedPromos.reduce((sum, p) => sum + p.discountAmount, 0)
  const adjustedTotal = Math.max(0, (amount ?? 0) - discountTotal)

  return (
    <>
      <Flex vertical gap={4}>
        {breakdown?.map((item) => (
          <Flex key={`${item?.label}-${item?.amount}`} align="center" justify="space-between">
            <Flex align="center" gap={4}>
              <Text>{item?.label}</Text>
              {item?.pricingDetails && <FlatRatePriceTooltip {...item.pricingDetails} />}
            </Flex>
            <Text>{item?.amount}</Text>
          </Flex>
        ))}
        {appliedPromos.map((promo) => (
          <Flex key={promo.code} align="center" justify="space-between">
            <Text type="secondary">{promo.name} ({promo.code})</Text>
            <Text style={{ color: "var(--color-primary)" }}>-{numberToCurrency(promo.discountAmount)}</Text>
          </Flex>
        ))}
      </Flex>
      <Divider />
      <Flex align="center" justify="space-between">
        <Text strong style={{ fontWeight: 600 }}>
          Total
        </Text>
        <Text style={{ fontWeight: 600 }}>{numberToCurrency(adjustedTotal)}</Text>
      </Flex>
      {selectedPayment && selectedPayment !== "full" && (
        <>
          <Flex align="center" justify="space-between">
            <Text strong style={{ fontWeight: 600 }}>
              Pay Now (Deposit)
            </Text>
            <Text style={{ fontWeight: 600 }}>{numberToCurrency(depositAmount)}</Text>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text>
              {selectedPayment === "later"
                ? `Balance Due on ${moment(bookingDate).subtract(1, "day").format("MMMM D, YYYY")}`
                : `Balance Due on ${moment(bookingDate).format("MMMM D, YYYY")}`}
            </Text>
            <Text>{`$ ${Number.parseFloat(balanceAmount || 0).toFixed(2)}`}</Text>
          </Flex>
        </>
      )}
    </>
  )
}

PriceBreakdown.propTypes = {
  breakdown: PropTypes.array,
  amount: PropTypes.number,
  depositAmount: PropTypes.number,
  balanceAmount: PropTypes.number,
  selectedPayment: PropTypes.string,
  bookingDate: PropTypes.string,
  appliedPromos: PropTypes.array
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const BookingDetailCard = ({ bookNow, trip, bookingButtonVisible, activeTab, bookingErrors, setBookingErrors, form, mode, embedded, appliedPromos, onPromoApplied, onPromoRemoved, clientIdentifier }) => {
  const [slotsFetching, setSlotsFetching] = useState(false)
  const selectedPayment = Form.useWatch("paymentMethod", form)
  const navigate = useNavigate()
  const { uuid, id } = useParams()
  const location = useLocation()
  const bookingId = location.state?.b_uuid
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const isBookNow = location?.pathname?.includes("/experience_detail") || location?.pathname?.includes("/trips")

  const {
    experienceById: { experienceDetail },
    experienceBookingData,
    experienceBookingData: { bookingSelections }
  } = useSelector((state) => state.experiences)

  const userRole = useSelector((state) => state.user.role)

  const promoDiscount = appliedPromos?.reduce((sum, p) => sum + p.discountAmount, 0) ?? 0
  const discountedAmount = Math.max(0, (bookingSelections?.amount || 0) - promoDiscount)
  const depositAmount = (discountedAmount * (experienceDetail?.deposit_percentage || 0)) / 100
  const balanceAmount = discountedAmount - depositAmount

  const calculateBookingTotal = useCallback(
    (counts, guideFeePercent, customFees = [], platformFeeSetting = null) => {
      let total = 0
      const breakdown = []

      const priceData = bookingSelections?.tripLength?.price || {}
      const {
        pricing_type,
        flat_rate,
        price_per_person,
        price_per_child,
        price_per_infant,
        price_per_passenger,
        included_guests,
        max_guests
      } = priceData

      const getPrice = (specificPrice) => Number.parseFloat(specificPrice ?? 0)

      const totalCounts = counts?.main || { adults: 0, children: 0, infants: 0, passengers: 0 }

      if (pricing_type === "charge_flat_rate") {
        const flatAmount = Number.parseFloat(flat_rate ?? 0)
        const totalGuests = totalCounts.adults + totalCounts.children + totalCounts.infants + totalCounts.passengers
        const hasGuests = totalGuests > 0 || totalCounts.passengers > 0

        if (hasGuests) {
          total += flatAmount
          const guestsIncluded = included_guests ?? max_guests ?? 0
          let remainingCapacity = guestsIncluded

          const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

          const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
          remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

          const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

          const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
          const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
          const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

          breakdown.push({
            label: "Flat Rate",
            amount: numberToCurrency(flatAmount),
            pricingDetails: { pricing_type, included_guests: guestsIncluded, price_per_person, price_per_child, price_per_infant }
          })

          if (extraAdults > 0) {
            const lineTotal = extraAdults * getPrice(price_per_person)
            total += lineTotal
            breakdown.push({ label: `Extra Adult x ${extraAdults}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraChildren > 0) {
            const lineTotal = extraChildren * getPrice(price_per_child)
            total += lineTotal
            breakdown.push({ label: `Extra Child x ${extraChildren}`, amount: numberToCurrency(lineTotal) })
          }
          if (extraInfants > 0) {
            const lineTotal = extraInfants * getPrice(price_per_infant)
            total += lineTotal
            breakdown.push({ label: `Extra Infant x ${extraInfants}`, amount: numberToCurrency(lineTotal) })
          }
          if (totalCounts.passengers > 0) {
            const lineTotal = totalCounts.passengers * getPrice(price_per_passenger)
            total += lineTotal
            breakdown.push({ label: `Passenger x ${totalCounts.passengers}`, amount: numberToCurrency(lineTotal) })
          }
        }
      } else {
        const mapping = [
          { count: totalCounts.adults, label: "Adult", price: price_per_person },
          { count: totalCounts.children, label: "Child", price: price_per_child },
          { count: totalCounts.infants, label: "Infant", price: price_per_infant },
          { count: totalCounts.passengers, label: "Passenger", price: price_per_passenger }
        ]

        for (const { count, label, price } of mapping) {
          if (count > 0) {
            const lineTotal = count * getPrice(price)
            total += lineTotal
            breakdown.push({ label: `${label} x ${count}`, amount: numberToCurrency(lineTotal) })
          }
        }
      }

      // Save guest subtotal before fees
      const guestSubtotal = total

      // Custom fees - collected, not pushed individually
      const customFeeDetails = []
      let customFeesTotal = 0
      if (guestSubtotal > 0 && customFees?.length > 0) {
        for (const fee of customFees) {
          const feeAmount = fee.fee_type === "percentage" ? (guestSubtotal * Number(fee.price)) / 100 : Number(fee.price)
          total += feeAmount
          customFeesTotal += feeAmount
          customFeeDetails.push({ label: fee.title, amount: numberToCurrency(feeAmount) })
        }
      }

      // Platform booking fee (apply split setting if configured)
      let taxAmount = 0
      if (guideFeePercent && guestSubtotal > 0) {
        const rawTaxAmount = (guestSubtotal * guideFeePercent) / 100
        taxAmount = applyPlatformFeeSetting(rawTaxAmount, platformFeeSetting)
        total += taxAmount
      }

      // Combined Tax & Fees line with info breakdown
      const combinedTaxTotal = customFeesTotal + taxAmount
      if (combinedTaxTotal > 0) {
        const infoItems = [...customFeeDetails, ...(taxAmount > 0 ? [{ label: "Booking Fee", amount: numberToCurrency(taxAmount) }] : [])]
        breakdown.push({
          label: "Tax & Fees",
          amount: numberToCurrency(combinedTaxTotal),
          info: infoItems.length > 1 ? infoItems : undefined
        })
      }

      return { total, guestSubtotal, breakdown }
    },
    [bookingSelections?.tripLength?.price]
  )

  useEffect(() => {
    const tripPricing = bookingSelections?.tripLength?.price
    const counts = bookingSelections?.counts
    if (tripPricing && counts) {
      const { total, guestSubtotal, breakdown } = calculateBookingTotal(
        counts,
        trip?.guide_booking_fee_percentage,
        experienceDetail?.assigned_custom_fees,
        experienceDetail?.platform_fee_setting
      )
      dispatch(updateBookingSelections({ amount: total, baseAmount: guestSubtotal, breakdown }))
    }
  }, [
    bookingSelections?.tripLength,
    bookingSelections?.counts,
    calculateBookingTotal,
    dispatch,
    trip?.guide_booking_fee_percentage,
    experienceDetail?.assigned_custom_fees,
    experienceDetail?.platform_fee_setting
  ])

  const handleDateChange = useCallback(
    (value) => {
      const tripDurations = experienceBookingData?.bookingData?.trip_durations
      const autoTripLength = tripDurations?.length === 1 ? tripDurations[0] : null
      const formattedDate = dayjs(value).format("YYYY-MM-DD")

      dispatch(
        updateBookingSelections({
          date: formattedDate,
          tripLength: autoTripLength,
          timeSlot: null,
          counts: EMPTY_COUNTS
        })
      )

      if (autoTripLength?.key) {
        setSlotsFetching(true)
        dispatch(fetchExperienceBookingDetails(id, "available_slots", autoTripLength.key, formattedDate, "", mode))
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, date: false, tripLength: false, timeSlot: false, counts: false }))
      }
    },
    [dispatch, id, mode, setBookingErrors, experienceBookingData?.bookingData?.trip_durations]
  )

  const handleTripLengthChange = useCallback(
    (tripLength) => {
      const hasCounts = Object.values(bookingSelections?.counts?.main ?? {}).some((v) => v > 0)
      dispatch(
        updateBookingSelections({
          tripLength,
          timeSlot: null,
          counts: hasCounts
            ? bookingSelections?.counts
            : {
                main: { adults: experienceBookingData?.bookingData?.min_guests || 0, children: 0, infants: 0, passengers: 0 },
                extra: { adults: 0, children: 0, infants: 0, passengers: 0 }
              }
        })
      )

      if (tripLength?.key && bookingSelections?.date) {
        setSlotsFetching(true)
        dispatch(
          fetchExperienceBookingDetails(
            id,
            "available_slots",
            tripLength?.key,
            dayjs(bookingSelections?.date).format("YYYY-MM-DD"),
            "",
            mode
          )
        )
      }

      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, tripLength: false }))
      }
    },
    [
      dispatch,
      bookingSelections?.date,
      bookingSelections?.counts,
      mode,
      id,
      setBookingErrors,
      experienceBookingData?.bookingData?.min_guests
    ]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      dispatch(updateBookingSelections({ timeSlot: value }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, timeSlot: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      dispatch(updateBookingSelections({ counts: newCounts }))
      if (setBookingErrors) {
        setBookingErrors((prev) => ({ ...prev, counts: false }))
      }
    },
    [dispatch, setBookingErrors]
  )

  const handleBookNow = useCallback(() => {
    if (!isBookNow) return

    const { date, counts, tripLength, timeSlot } = bookingSelections
    const minGuests = experienceBookingData?.bookingData?.min_guests || 1
    const totalGuests = ["adults", "children", "infants", "passengers"]
      .map((key) => Number(counts?.main?.[key] || 0))
      .reduce((acc, val) => acc + val, 0)
    const errors = {
      date: !date,
      counts: !counts?.main || totalGuests < minGuests,
      tripLength: !tripLength,
      timeSlot: !timeSlot
    }
    const hasErrors = Object.values(errors).some(Boolean)

    if (hasErrors) {
      setBookingErrors(errors)
      return
    }

    setBookingErrors({ date: false, counts: false, tripLength: false, timeSlot: false })

    if (mode === BOOKING_MODES.embedded || !mode) {
      navigate(routes.embeddedExperienceBooking({ uuid, id }))
    } else {
      const isClientUser = userRole === "user"
      if (isClientUser) navigate(routes.clientBookingWizardRootPath({ id }))
      else navigate({ pathname: oldRoutes.bookingWizardPath({ id }) })
    }
  }, [isBookNow, bookingSelections, experienceBookingData?.bookingData?.min_guests, setBookingErrors, mode, userRole, navigate, id, uuid])

  useEffect(() => {
    if (bookNow) {
      if (id) {
        dispatch(fetchExperienceBookingDetails(id, "trip_data", "", "", bookingId, mode))
      }
      if (isBookNow) {
        dispatch(resetBookingSelections())
      }
    }
  }, [bookNow, dispatch, id, bookingId, mode, isBookNow])

  useEffect(() => {
    const tripDurations = experienceBookingData?.bookingData?.trip_durations
    if (
      tripDurations?.length === 1 &&
      (!bookingSelections?.tripLength || bookingSelections?.tripLength?.value !== tripDurations?.[0]?.value)
    ) {
      handleTripLengthChange(tripDurations?.[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.trip_durations])

  useEffect(() => {
    const timeSlots = experienceBookingData?.bookingData?.data
    if (timeSlots?.length === 1 && !bookingSelections?.timeSlot && !experienceBookingData?.loading) {
      handleTimeSlotChange(timeSlots[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experienceBookingData?.bookingData?.data, experienceBookingData?.loading, bookingSelections?.timeSlot])

  useEffect(() => {
    if (!experienceBookingData?.loading) {
      setSlotsFetching(false)
    }
  }, [experienceBookingData?.loading])

  const tripDurations = experienceBookingData?.bookingData?.trip_durations
  const hasMultipleDurations = tripDurations?.length > 1
  const showTripLength = !!bookingSelections?.date && hasMultipleDurations
  const showTime = !!bookingSelections?.date && !!bookingSelections?.tripLength && !experienceBookingData?.loading && !slotsFetching
  const showGuests = showTime && !!bookingSelections?.timeSlot
  const timeSlots = experienceBookingData?.bookingData?.data ?? []

  return (
    <div className={styles.bookingDetailCardSpinner}>
      <Spin spinning={experienceBookingData?.loading}>
        <div className={`${styles.bookingCard} ${embedded ? styles.embeddedBookingCard : ""}`}>
          {!hasMultipleDurations && tripDurations?.length === 1 && <Text size="lg">{tripDurations[0]?.value}</Text>}
          <Flex gap={8} align="center">
            <Text fw={600} size="lg">
              {bookNow ? (trip?.title ?? "") : `From $${trip?.price_per_person}`}
            </Text>
            <PricingDetailsTooltip tripPrices={trip?.trip_prices} />
          </Flex>
          <Flex vertical gap={8} className={embedded ? "py-xs" : "py-xl"}>
            {bookNow === false ? (
              INFO_DATA.map((data, index) => (
                <Flex justify="space-between" align="center" className={`${styles.cardDetailItems} p-s rounded-s`} key={index + 1}>
                  <Text>{data.label}</Text>
                  <Text>{data.value}</Text>
                </Flex>
              ))
            ) : (
              <>
                <DateSelector
                  value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
                  onChange={handleDateChange}
                  activeTab={activeTab}
                  error={bookingErrors?.date}
                  trip={trip}
                />
                {showTripLength && (
                  <PillSelector
                    key="trip-length"
                    label="Trip Length"
                    options={tripDurations}
                    selectedValue={bookingSelections.tripLength?.value}
                    onSelect={handleTripLengthChange}
                    error={bookingErrors?.tripLength}
                    errorMessage="Trip Length is required."
                    activeTab={activeTab}
                    embedded
                  />
                )}
                {showTime && (
                  <TimeSlotSelector
                    key="time-slot"
                    slots={timeSlots}
                    selectedSlot={bookingSelections?.timeSlot}
                    onSelect={handleTimeSlotChange}
                    error={bookingErrors?.timeSlot}
                    activeTab={activeTab}
                    embedded
                  />
                )}
                {showGuests && (
                  <div key="guests" className={styles.stepFadeIn}>
                    <GuestsSelector
                      counts={bookingSelections?.counts}
                      setCounts={handleGuestsChange}
                      activeTab={activeTab}
                      error={bookingErrors?.counts}
                      setBookingErrors={setBookingErrors}
                      maxGuests={experienceBookingData?.bookingData?.max_guests}
                      minGuests={experienceBookingData?.bookingData?.min_guests}
                    />
                  </div>
                )}
              </>
            )}
          </Flex>
          {bookNow && showGuests && (
            <>
              {activeTab === 2 && (
                <PromoCodeInput
                  totalAmount={bookingSelections?.baseAmount ?? 0}
                  appliedPromos={appliedPromos}
                  onPromoApplied={onPromoApplied}
                  onPromoRemoved={onPromoRemoved}
                  experienceId={id}
                  clientIdentifier={clientIdentifier}
                />
              )}
              <PriceBreakdown
                breakdown={bookingSelections?.breakdown}
                amount={bookingSelections?.amount}
                depositAmount={depositAmount}
                balanceAmount={balanceAmount}
                selectedPayment={selectedPayment}
                bookingDate={bookingSelections?.date}
                appliedPromos={appliedPromos}
              />
            </>
          )}
          {bookingButtonVisible && (
            <>
              <Button type="primary" disabled={!bookNow} className={styles.bookButton} onClick={handleBookNow}>
                Book Now
              </Button>
              <Flex justify="center" style={{ marginTop: "var(--spacing-xxxs)" }}>
                <Text style={{ fontSize: "var(--font-size-s)" }}>{t("webIntegration.payment_info")}</Text>
              </Flex>
            </>
          )}
        </div>
      </Spin>
    </div>
  )
}

BookingDetailCard.propTypes = {
  bookNow: PropTypes.bool,
  form: PropTypes.object,
  trip: PropTypes.shape({
    min_price: PropTypes.number,
    title: PropTypes.string,
    guide_booking_fee_percentage: PropTypes.number,
    price_per_person: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    trip_prices: PropTypes.array
  }),
  bookingButtonVisible: PropTypes.bool,
  activeTab: PropTypes.number,
  bookingErrors: PropTypes.object,
  setBookingErrors: PropTypes.func,
  mode: PropTypes.string,
  embedded: PropTypes.bool,
  appliedPromos: PropTypes.array,
  onPromoApplied: PropTypes.func,
  onPromoRemoved: PropTypes.func,
  clientIdentifier: PropTypes.string
}

export default BookingDetailCard
